/**
 * Client side of the preview snapshot pipeline (docs/PREVIEW_SNAPSHOTS.md).
 *
 * A url doc (site or test) may carry:
 *   previewUrl     long-lived WebP URL, 1200x750, top of the page
 *   previewColors  2 or 3 hex colours for the gradient wash
 *   previewAt      Firestore Timestamp of the capture
 *   previewStatus  'queued' | 'ready' | 'skipped' | 'failed' (optional)
 *
 * Every field is optional: older docs, drafts, password-protected sites and
 * anything captured while the pipeline is off have none of them.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';

const HEX_RE = /^#[0-9a-f]{6}$/i;

/** The preview fields of a doc, normalised. Returns null when there is none. */
export function readPreview(doc) {
  if (!doc || typeof doc.previewUrl !== 'string' || !doc.previewUrl) return null;
  const colors = Array.isArray(doc.previewColors)
    ? doc.previewColors.filter((c) => typeof c === 'string' && HEX_RE.test(c)).slice(0, 3)
    : [];
  let at = null;
  if (doc.previewAt && typeof doc.previewAt.toDate === 'function') at = doc.previewAt.toDate();
  else if (doc.previewAt) at = new Date(doc.previewAt);
  return { url: doc.previewUrl, colors, at };
}

/**
 * Ask the server to capture a fresh preview ("Refresh preview").
 * Resolves { status }: 'queued' | 'recent' (asked within the last minute) |
 * 'skipped' (with reason, e.g. 'password') | 'disabled' (pipeline off).
 * ownerUid is needed only for a site shared with the caller.
 */
export async function requestPreviewRefresh(urlId, ownerUid) {
  const functions = getFunctions(undefined, 'us-central1');
  const refresh = httpsCallable(functions, 'refreshPreview');
  const { data } = await refresh(ownerUid ? { urlId, ownerUid } : { urlId });
  return data;
}
